/* React */
import React from "react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { setSelectedModel } from "src/redux/features/agent-chat/agentChatSlice";

/* Components */
import SimpleSelect from "src/components/shared/simple-select/SimpleSelect";

/* Interfaces */
import { AGENT_DEFAULT_MODEL_LABEL } from "src/shared/interfaces/api/AgentSpec";

/* Stylesheet */
import styles from "./ModelSelect.module.scss";

/**
 * LLM model picker.
 *
 * The first entry sends no model at all, leaving the choice to the
 * deployment, and it is the default on purpose: `/models/allowed`
 * lists `test` first, so selecting the first allowed model would
 * quietly put every user on the keyword generator instead of the
 * configured provider model.
 *
 * Naming a model explicitly is mostly for testing — the acceptance
 * suite picks `test` here, which makes the browser tests
 * deterministic and stops them spending provider calls.
 */
const ModelSelect: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { allowedModels, selectedModel, isAnswering } =
    useSelector((state: RootState) => state.agentChat);

  /* Nothing to choose between until the allowed models arrive. */
  if (allowedModels.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="model-select"
      className={styles.model_select}
    >
      {/* `null` is the deployment-default entry, and it is an item in its
          own right rather than a reserved label. Comparing the chosen
          label against a magic string would mean a model actually named
          "Deployment default" got coerced to null — raised in review. */}
      <SimpleSelect<string | null>
        placeholder="Model"
        value={selectedModel ?? AGENT_DEFAULT_MODEL_LABEL}
        items={[null, ...allowedModels]}
        disabled={isAnswering}
        getLabel={(model: string | null) =>
          model ?? AGENT_DEFAULT_MODEL_LABEL
        }
        onSelect={(model: string | null) =>
          dispatch(setSelectedModel(model))
        }
      />
    </div>
  );
};

export default ModelSelect;
